/**
 * Sidebar of the application shell.
 *
 * Behaviour that the layout depends on:
 * - it becomes an off-canvas drawer below `lg`, and a fixed rail above it, so the same markup serves both;
 * - it collapses to an icon-only rail on desktop, with each icon keeping an accessible name (a tooltip-shaped UI
 *   without a label is unusable with a screen reader);
 * - only icon *direction* is mirrored in Persian; the items themselves are positioned with logical properties.
 */

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  accountNav,
  visibleNavItems,
  workspaceNav,
  type NavItem,
} from '@/components/layout/navItems';
import { useCurrentUser } from '@/features/auth/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

/** Props accepted by {@link Sidebar}. */
export interface SidebarProps {
  /** Extra classes for the root element. */
  className?: string;
}

/** Renders one navigation entry. */
function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useTranslation();
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen);
  const Icon = item.icon;

  return (
    <li>
      <NavLink
        to={item.to}
        // `end` keeps `/dashboard` from matching every nested route of the prefix.
        end={item.to === '/dashboard'}
        title={collapsed ? t(item.labelKey) : undefined}
        onClick={() => setMobileNavOpen(false)}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'text-sidebar-foreground/80 hover:bg-white/5 hover:text-sidebar-foreground',
            isActive && 'bg-white/10 text-sidebar-foreground',
            collapsed && 'justify-center px-0',
          )
        }
      >
        <Icon aria-hidden className="h-4 w-4 shrink-0" />
        <span className={cn('truncate', collapsed && 'sr-only')}>{t(item.labelKey)}</span>
      </NavLink>
    </li>
  );
}

/** Primary navigation panel, shared by the desktop rail and the mobile drawer. */
export function Sidebar({ className }: SidebarProps) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);

  const workspace = visibleNavItems(workspaceNav, user);
  const account = visibleNavItems(accountNav, user);

  return (
    <nav
      aria-label={t('nav.workspaceSection')}
      className={cn('flex h-full flex-col gap-6 bg-sidebar p-4 text-sidebar-foreground', className)}
    >
      <div className={cn('flex items-center gap-2 px-2', collapsed && 'justify-center px-0')}>
        <span
          aria-hidden
          className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground"
        >
          PF
        </span>
        <span className={cn('text-base font-semibold', collapsed && 'sr-only')}>
          {t('common.appName')}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h2
            className={cn(
              'px-3 text-xs font-semibold uppercase tracking-wide opacity-60',
              collapsed && 'sr-only',
            )}
          >
            {t('nav.workspaceSection')}
          </h2>
          <ul className="flex flex-col gap-1">
            {workspace.map((item) => (
              <SidebarLink key={item.key} item={item} collapsed={collapsed} />
            ))}
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h2
            className={cn(
              'px-3 text-xs font-semibold uppercase tracking-wide opacity-60',
              collapsed && 'sr-only',
            )}
          >
            {t('nav.accountSection')}
          </h2>
          <ul className="flex flex-col gap-1">
            {account.map((item) => (
              <SidebarLink key={item.key} item={item} collapsed={collapsed} />
            ))}
          </ul>
        </section>
      </div>

      <p className={cn('px-3 text-xs opacity-60', collapsed && 'sr-only')}>
        {t('common.version')} 1.0.0
      </p>
    </nav>
  );
}
